<script lang="ts">
	import { enhance } from '$app/forms';
	import { page } from '$app/state';
	import BtnPrimary from '$lib/buttons/v2/btnPrimary.svelte';
	import Checkmark from '$lib/icons/checkmark.svelte';
	import { trackEvent } from '$lib/segment/client';
	import { event } from '$lib/utils/tracking';
	import type { ActionResult } from '@sveltejs/kit';
	import type { HTMLAttributes } from 'svelte/elements';
	import { fly, slide } from 'svelte/transition';

	type FormState = 'processing' | 'success' | 'error';

	interface Props extends HTMLAttributes<HTMLInputElement> {
		name: string;
		onsuccess?: () => void;
	}

	let { onsuccess, children, name, ...other }: Props = $props();

	let error = $state<string | null>(null);
	let formState = $state<FormState | null>(null);

	const handleEnhance = () => {
		formState = 'processing';

		return (response: { result: ActionResult }) => {
			event('Email Footer Submit Button Clicked');

			const { result } = response;

			if (result.type === 'failure') {
				error = result.data?.message || null;
				formState = 'error';
				return;
			}

			if (result.type !== 'success') {
				formState = 'error';
				return;
			}

			formState = 'success';
			onsuccess?.();

			trackEvent('Address email entered');
		};
	};

	let successfullyEntered = $derived(formState === 'success');
</script>

<form class="relative" method="POST" action="/?/newsletter" use:enhance={handleEnhance}>
	<input type="hidden" name="pathname" value={page.url.pathname} />
	<input
		{...other}
		{name}
		required
		type="email"
		inputmode="email"
		autocomplete="email"
		class={[
			'w-full rounded-full py-[18px] pl-6 pr-[120px] text-[18px]/[140%] text-brown-2 outline-none transition-colors duration-500 placeholder:text-brown-2 sm:pr-[200px]',
			{
				'text-stone-5 placeholder:text-stone-5': successfullyEntered
			}
		]}
	/>
	<div
		class={[
			'absolute right-0 top-0 px-[5px] py-[5px] transition-all duration-500',
			{
				'w-full text-green-5': successfullyEntered,
				'w-[120px] sm:w-[200px]': !successfullyEntered
			}
		]}
	>
		<BtnPrimary
			class="w-full"
			type="submit"
			disabled={formState === 'processing'}
			title="Sign up for updates"
		>
			Submit
		</BtnPrimary>

		<div
			class={[
				'absolute inset-0 mx-[5px] my-[5px] flex items-center justify-center gap-x-2 rounded-full bg-green-3 font-semibold text-green-5 transition-opacity duration-300',
				{
					'z-50 opacity-0': !successfullyEntered
				}
			]}
		>
			{#if successfullyEntered}
				<span in:fly={{ x: 30, duration: 400, delay: 100 }}>
					<Checkmark class="h-6 w-6" />
				</span>
			{/if}
			Success!
		</div>
	</div>
</form>

{#if formState === 'error' && error}
	<p class="p-2 text-red-500" transition:slide>{error}</p>
{/if}

{@render children?.()}
